import { joinUrl } from '@xingternal/crate-core-assets';
import type { Manifest } from '@xingternal/crate-core-assets';
import { loadExternals } from '@xingternal/crate-runtime';
import { Eventual } from '@xingternal/crate-xinglet';
import type {
  GlobalScope,
  I18nModule,
  InternalHost,
  XingletClass,
  XingletModule,
} from '@xingternal/crate-xinglet';

import {
  importModule,
  loadScript,
  loadStyle,
  tryGetModule,
} from './import-module';

declare const globalThis: GlobalScope;

async function importXingletAssets(
  manifest: Manifest,
  assetsBaseUrl: string
): Promise<void> {
  const scripts = manifest.assets
    .filter((asset) => /\.js(\?\d+)?$/.test(asset))
    .map((asset) => loadScript(joinUrl(assetsBaseUrl, asset)));

  const styles = manifest.assets
    .filter((asset) => /\.css(\?\d+)?$/.test(asset))
    .map(async (asset) => {
      try {
        loadStyle(joinUrl(assetsBaseUrl, asset));
      } catch (err) {
        console.error(err);
      }
    });

  await Promise.all([...scripts, ...styles]);
}

export function importXinglet(
  host: InternalHost,
  name: string
): Eventual<XingletClass> {
  return Eventual.resolve(tryGetModule<XingletModule>(name, 'main')).then(
    async (module) => {
      if (!module) {
        const { assetsBaseUrl, language } = host.config;
        const manifest = host.runtime.manifest[name];
        const languageEntry = `lang-${language}`;

        if (!manifest) {
          throw new Error(`Could not find manifest for xinglet "${name}"`);
        }

        await loadExternals(
          globalThis.crateImports,
          manifest.metadata.dependencies
        );
        await importXingletAssets(manifest, assetsBaseUrl);

        if (manifest.chunks[languageEntry]) {
          const { messages } = await importModule<I18nModule>(
            host,
            name,
            languageEntry
          );

          Object.assign(host.runtime.languageData, messages);
        }

        module = await importModule<XingletModule>(host, name, 'main');

        if (!module) {
          throw new Error(`Could not load xinglet "${name}"`);
        }
      }

      return module.default;
    }
  );
}
